import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { PlateCells } from "@/components/designer/RoomPreview";
import { api, errMsg } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { LAYOUT_LABEL } from "@/lib/designer";
import { formatCOP, useSeo } from "@/lib/site";

const FIELDS = [
  ["name", "Nombre completo", "text", "name"], ["email", "Correo electrónico", "email", "email"], ["phone", "Teléfono / WhatsApp", "tel", "tel"],
  ["address", "Dirección de envío", "text", "street-address"], ["city", "Ciudad", "text", "address-level2"], ["department", "Departamento", "text", "address-level1"],
];
const PENDING_KEY = "lumina_pending_payment";

export const openWompi = (url, fields) => {
  const form = document.createElement("form");
  form.method = "GET"; form.action = url; form.target = "_blank";
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden"; input.name = name; input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  form.remove();
};

const Thumb = ({ item, templates, photos }) => {
  const s = 64 / Math.max(item.width_cm, item.height_cm);
  const w = item.width_cm * s, h = item.height_cm * s;
  return (
    <div className="grid h-[76px] w-[76px] shrink-0 place-items-center bg-[#EAE7E2]">
      <div className="relative overflow-hidden" style={{ width: w, height: h, borderRadius: 3 }}>
        <PlateCells w={w} h={h} rects={templates[item.layout][item.orientation]} cells={item.cells} photos={photos} orientation={item.orientation} />
      </div>
    </div>
  );
};

const Waiting = ({ reference }) => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["pay-status", reference], queryFn: () => api.get(`/checkout/status/${reference}`).then((r) => r.data), refetchInterval: 4000 });
  useEffect(() => {
    if (data?.status === "APPROVED") { localStorage.removeItem(PENDING_KEY); qc.invalidateQueries({ queryKey: ["cart"] }); navigate(`/pedido/resultado?ref=${reference}`); }
  }, [data, reference, navigate, qc]);
  return (
    <div data-testid="checkout-waiting" className="mt-6 border border-[#B18A4B]/40 bg-[#D9B978]/10 p-5 text-sm leading-relaxed">
      <p className="flex items-center gap-2 font-medium"><Loader2 className="h-4 w-4 animate-spin" /> Completa el pago en la pestaña de Wompi.</p>
      <p className="mt-2 text-[#080808]/70">Esta página se actualizará sola cuando Wompi confirme el pago. {data?.status && data.status !== "PENDING" && data.status !== "APPROVED" && <b className="text-[#8B1E1E]">Wompi informó: pago no aprobado. Puedes intentarlo de nuevo.</b>}</p>
    </div>
  );
};

export default function CheckoutPage() {
  useSeo({ title: "Finalizar compra | LUMINA STUDIO", noindex: true });
  const { data: cart, isLoading } = useCart();
  const { data: cfg } = useQuery({ queryKey: ["designer-config"], queryFn: () => api.get("/designer/config").then((r) => r.data) });
  const { data: mode } = useQuery({ queryKey: ["checkout-mode"], queryFn: () => api.get("/checkout/mode").then((r) => r.data), staleTime: 0 });
  const { data: photoList = [] } = useQuery({ queryKey: ["designer-photos"], queryFn: () => api.get("/designer/photos").then((r) => r.data), enabled: !!cart?.items.length });
  const photos = useMemo(() => Object.fromEntries(photoList.map((p) => [p.id, p])), [photoList]);
  const [f, setF] = useState(() => JSON.parse(localStorage.getItem("lumina_customer") || "{}"));
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState(() => localStorage.getItem(PENDING_KEY));
  if (isLoading || !cfg) return <div className="min-h-[60vh]" />;
  if (!cart.items.length && !reference) return (
    <div className="mx-auto max-w-xl px-5 py-24 text-center"><p data-testid="checkout-empty">Tu carrito está vacío.</p>
      <Link to="/personalizar" className="mt-6 inline-block underline">Personaliza tu LUMINA</Link></div>
  );
  const pending = cart.items.some((i) => i.has_pending_changes);
  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      localStorage.setItem("lumina_customer", JSON.stringify(f));
      const { data } = await api.post("/checkout", { ...f, notes: f.notes || "" });
      openWompi(data.checkout_url, data.fields);
      localStorage.setItem(PENDING_KEY, data.reference);
      setReference(data.reference);
    } catch (err) { toast.error(err.response?.status === 422 ? "Revisa tus datos: correo válido, teléfono solo con números y dirección completa." : errMsg(err)); }
    setBusy(false);
  };
  return (
    <div data-testid="checkout-page" className="mx-auto grid max-w-[1200px] gap-10 px-5 py-12 sm:px-8 sm:py-16 lg:grid-cols-[1fr_400px]">
      <form onSubmit={pay} data-testid="checkout-form">
        <h1 className="font-serif-display text-[34px] font-medium sm:text-[46px]">Finalizar compra</h1>
        {mode?.test && (
          <p data-testid="checkout-test-mode-banner" className="mt-4 rounded-lg border border-[#B18A4B]/50 bg-[#FBF6EC] px-4 py-3 text-sm text-[#5C4520]">
            <b>Modo prueba · sin cobro.</b> Entraste con tu cuenta del panel, así que este pago usa las llaves de pruebas de Wompi y no cobra dinero real. Para un pago aprobado usa la tarjeta 4242 4242 4242 4242, cualquier fecha futura y cualquier CVC.
            {!mode.ready && " Aún faltan las llaves de pruebas: agrégalas o cierra sesión del panel para pagar de verdad."}
          </p>
        )}
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([k, label, type, ac]) => (
            <label key={k} className={`text-xs font-semibold uppercase tracking-[0.12em] ${k === "address" ? "sm:col-span-2" : ""}`}>{label}
              <Input required type={type} autoComplete={ac} data-testid={`checkout-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5 bg-white text-base font-normal normal-case tracking-normal" />
            </label>
          ))}
          <label className="text-xs font-semibold uppercase tracking-[0.12em] sm:col-span-2">Notas de entrega (opcional)
            <Input data-testid="checkout-notes" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5 bg-white text-base font-normal normal-case tracking-normal" />
          </label>
        </div>
        <p className="mt-5 text-sm text-[#080808]/65">Envío contra entrega: el valor del envío se paga al recibir tu pedido.</p>
        {pending && <p data-testid="checkout-pending-warning" className="mt-4 text-sm text-[#8B1E1E]">Un diseño tiene cambios sin confirmar. Confírmalo desde el carrito antes de pagar.</p>}
        <button type="submit" data-testid="pay-wompi-button" disabled={busy || pending || !cart.items.length}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#080808] py-4 text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#FAF8F4] transition-colors hover:bg-[#B18A4B] disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Pagar con Wompi
        </button>
        <p className="mt-2 text-center text-xs text-[#080808]/55">El pago se abre en una pestaña nueva de Wompi. Tu pedido se crea solo cuando Wompi aprueba el pago.</p>
        {reference && <Waiting reference={reference} />}
      </form>
      <aside data-testid="checkout-summary" className="self-start bg-white p-6 lg:sticky lg:top-28">
        <h2 className="text-sm font-semibold uppercase tracking-[0.12em]">Tu pedido</h2>
        <ul className="mt-4 divide-y divide-[#EAE7E2]">
          {cart.items.map((i) => (
            <li key={i.id} data-testid={`checkout-item-${i.id}`} className="flex gap-4 py-4">
              <Thumb item={i} templates={cfg.templates} photos={photos} />
              <div className="text-sm">
                <p className="font-serif-display text-xl">LUMINA {i.size} · x{i.quantity}</p>
                <p className="text-[#080808]/65">{i.width_cm} × {i.height_cm} cm · {i.orientation === "vertical" ? "Vertical" : "Horizontal"} · {i.layout === 1 ? "Foto individual" : LAYOUT_LABEL[i.layout]}</p>
                <p className="mt-1 font-medium">{formatCOP(i.line_total_cop)}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex items-baseline justify-between border-t border-[#EAE7E2] pt-4">
          <span className="text-sm font-semibold uppercase tracking-[0.12em]">Total</span>
          <span data-testid="checkout-total" className="font-serif-display text-3xl">{formatCOP(cart.subtotal_cop)}</span>
        </div>
        <p className="mt-1 text-xs text-[#080808]/55">Envío contra entrega</p>
      </aside>
    </div>
  );
}
